export const AUTH_FLOW_VIEWED_EVENT = "auth_flow_viewed";
export const AUTH_FLOW_ERROR_EVENT = "auth_flow_error";
export const AUTH_FLOW_ERROR_DEDUPE_WINDOW_MS = 2_000;
export const TURNSTILE_MIN_WIDGET_WIDTH_PX = 300;
export const AUTH_FLOW_MESSAGE_MAX_LENGTH = 500;

export type AuthFlow = "sign_up" | "sign_in";

export type AuthFlowErrorType =
  | "turnstile_challenge"
  | "captcha_failed_to_load"
  | "cloudflare_script_blocked"
  | "clerk_validation";

export type AuthFlowSuspectedCause =
  | "script_blocked"
  | "turnstile_bot_or_integrity_check"
  | "in_app_browser"
  | "automation"
  | "widget_too_narrow"
  | "clerk_validation";

export type AuthFlowErrorClassification = {
  errorType: AuthFlowErrorType;
  errorCode: string | null;
};

export type AuthFlowErrorReportProperties = {
  flow: AuthFlow;
  pathname: string;
  error_type: AuthFlowErrorType;
  error_code: string | null;
  suspected_cause: AuthFlowSuspectedCause;
  message: string;
  user_agent: string;
  viewport_width: number;
  captcha_container_width: number | null;
  in_app_browser: boolean;
  automation: boolean;
  session_id: string;
  failed_script_src: string | null;
};

export type AuthFlowErrorReport = {
  event: typeof AUTH_FLOW_ERROR_EVENT;
  properties: AuthFlowErrorReportProperties;
};

const IN_APP_BROWSER_PATTERNS = [
  /Instagram/i,
  /FBAN|FBAV/i,
  /TikTok/i,
  /BytedanceWebview/i,
];

function normalizePathname(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;
}

export function getAuthFlowFromPathname(
  pathname: string | null | undefined,
): AuthFlow | null {
  if (!pathname) {
    return null;
  }

  const normalized = normalizePathname(pathname);
  if (normalized === "/sign-up" || normalized.startsWith("/sign-up/")) {
    return "sign_up";
  }
  if (normalized === "/sign-in" || normalized.startsWith("/sign-in/")) {
    return "sign_in";
  }
  return null;
}

export function parseTurnstileErrorCode(
  message: string | null | undefined,
): string | null {
  if (!message) {
    return null;
  }
  const match = message.match(/\[Cloudflare Turnstile\] Error:\s*(\d+)/i);
  return match?.[1] ?? null;
}

export function isCloudflareChallengeScriptSrc(src: string | null | undefined): boolean {
  if (!src) {
    return false;
  }
  try {
    const url = new URL(src);
    return url.hostname === "challenges.cloudflare.com";
  } catch {
    return src.includes("challenges.cloudflare.com");
  }
}

export function classifyAuthFlowError(input: {
  message?: string | null;
  failedScriptSrc?: string | null;
}): AuthFlowErrorClassification | null {
  if (isCloudflareChallengeScriptSrc(input.failedScriptSrc)) {
    return {
      errorType: "cloudflare_script_blocked",
      errorCode: null,
    };
  }

  const message = input.message?.trim() ?? "";
  if (!message) {
    return null;
  }

  const turnstileCode = parseTurnstileErrorCode(message);
  if (turnstileCode) {
    return {
      errorType: "turnstile_challenge",
      errorCode: turnstileCode,
    };
  }

  if (message.includes("CAPTCHA failed to load")) {
    return {
      errorType: "captcha_failed_to_load",
      errorCode: null,
    };
  }

  return {
    errorType: "clerk_validation",
    errorCode: null,
  };
}

export function detectInAppBrowser(userAgent: string | null | undefined): boolean {
  if (!userAgent) {
    return false;
  }
  return IN_APP_BROWSER_PATTERNS.some((pattern) => pattern.test(userAgent));
}

export function detectAutomation(
  webdriver: boolean,
  userAgent: string | null | undefined,
): boolean {
  if (webdriver) {
    return true;
  }
  return Boolean(userAgent && /HeadlessChrome/i.test(userAgent));
}

export function inferSuspectedCause(input: {
  errorType: AuthFlowErrorType;
  errorCode: string | null;
  inAppBrowser: boolean;
  automation: boolean;
  captchaContainerWidth: number | null;
}): AuthFlowSuspectedCause {
  if (input.automation) {
    return "automation";
  }
  if (input.inAppBrowser) {
    return "in_app_browser";
  }
  if (input.errorType === "cloudflare_script_blocked") {
    return "script_blocked";
  }
  if (
    input.captchaContainerWidth != null &&
    input.captchaContainerWidth < TURNSTILE_MIN_WIDGET_WIDTH_PX
  ) {
    return "widget_too_narrow";
  }
  if (
    input.errorType === "turnstile_challenge" ||
    input.errorType === "captcha_failed_to_load"
  ) {
    return "turnstile_bot_or_integrity_check";
  }
  return "clerk_validation";
}

export function redactAuthFlowMessage(message: string): string {
  return message
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[redacted-email]")
    .replace(/(?:password|passwd|pwd)\s*[:=]\s*\S+/gi, "[redacted-secret]")
    .slice(0, AUTH_FLOW_MESSAGE_MAX_LENGTH);
}

export function buildAuthFlowErrorReport(input: {
  flow: AuthFlow;
  pathname: string;
  message: string;
  userAgent: string;
  webdriver: boolean;
  viewportWidth: number;
  captchaContainerWidth: number | null;
  sessionId: string;
  failedScriptSrc?: string | null;
}): AuthFlowErrorReport | null {
  const classified = classifyAuthFlowError({
    message: input.message,
    failedScriptSrc: input.failedScriptSrc,
  });
  if (!classified) {
    return null;
  }

  const inAppBrowser = detectInAppBrowser(input.userAgent);
  const automation = detectAutomation(input.webdriver, input.userAgent);
  const message = redactAuthFlowMessage(input.message.trim());

  return {
    event: AUTH_FLOW_ERROR_EVENT,
    properties: {
      flow: input.flow,
      pathname: input.pathname,
      error_type: classified.errorType,
      error_code: classified.errorCode,
      suspected_cause: inferSuspectedCause({
        errorType: classified.errorType,
        errorCode: classified.errorCode,
        inAppBrowser,
        automation,
        captchaContainerWidth: input.captchaContainerWidth,
      }),
      message,
      user_agent: input.userAgent.slice(0, AUTH_FLOW_MESSAGE_MAX_LENGTH),
      viewport_width: input.viewportWidth,
      captcha_container_width: input.captchaContainerWidth,
      in_app_browser: inAppBrowser,
      automation,
      session_id: input.sessionId,
      failed_script_src: input.failedScriptSrc ?? null,
    },
  };
}

export function isAuthFlowErrorDuplicate(
  previous: {
    errorType: AuthFlowErrorType | string;
    errorCode: string | null;
    message: string;
    at: number;
  },
  next: {
    errorType: AuthFlowErrorType | string;
    errorCode: string | null;
    message: string;
    at: number;
  },
  windowMs: number = AUTH_FLOW_ERROR_DEDUPE_WINDOW_MS,
): boolean {
  if (next.at - previous.at > windowMs) {
    return false;
  }
  return (
    previous.errorType === next.errorType &&
    previous.errorCode === next.errorCode &&
    previous.message === next.message
  );
}
